import type {
  ObjectType,
  NameProperties,
  RelatedObjectsMap,
} from '../../components/parser/ParserTypes';
import { type ObjectWithPillsMap } from '../../components/documentation/salesforce-documentation-card/types';
import type {
  DocumentationPills,
  DocumentationParsedData,
} from '../../components/documentation/types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '..';
import { DocumentationTabTypes } from '../../types/enums/DocumentationTabTypes';
import { getObjectsWithPillsPerCategory } from '../../components/documentation/cpq/utils';
import { formatCamelCase } from '../../components/documentation/universal-search/utils';
import isEqual from 'lodash/isEqual';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { sortLexicographically } from '../../lib/sortLexicographically';
import { translateTypeToHumanName } from '../../components/documentation/helpers';
import type { SalesforceItem } from '../../components/parser/ParserTypes';
import type { RelatedListProperties } from '@server/documentation';
import type {
  SweepElementsByKey,
  DocumentationElementDependencies,
  DocumentationSweepFieldsSummary,
} from '../../types/documentation/documentation';
import type { ObjectParsedDataByType } from '@server/parser';
import {
  calcConfigurationItemsFromParserResponse,
  calcParsedRecordTypes,
  calcLayouts,
} from '../../components/parser/parserUtils';

export interface LayoutRelatedListsAndButtons {
  lists: RelatedListProperties[];
  buttons: NameProperties[];
}

export type DocumentationLoadingState = 'idle' | 'loading' | 'success' | 'error';

export interface DocumentationLoadingStateEntry {
  status: Exclude<DocumentationLoadingState, 'idle'>;
}

export interface SalesforceDocumentationState {
  objects: ObjectWithPillsMap;
  transientObjects: { [crmOrgId: string]: { [objectName: string]: boolean } };
  singleObject?: {
    activeTab?: DocumentationTabTypes;
    objectTypeName?: ObjectTypeName;
  };
  activeObjectElementsRawData?: {
    [key in DocumentationTabTypes]: SalesforceItem[];
  };
  relatedObjectFilterOptions?: { label: string; value: string }[];
  configurationTypeFilterOptions?: { label: string; value: string }[];
  relatedListsAndButtons: { [metadataId: string]: LayoutRelatedListsAndButtons };
  showLoader: boolean;
  parsedByOrg: { [crmOrgId: string]: DocumentationParsedData };
  documentationLoadingStateByOrgId: { [crmOrgId: string]: DocumentationLoadingStateEntry };
  saltoObjectTypesByOrg: { [crmOrgId: string]: Pick<ObjectTypeName, 'objectType' | 'label'>[] };
  sweepElementsByOrg: { [crmOrgId: string]: SweepElementsByKey };
  sweepFieldsByOrg: { [crmOrgId: string]: DocumentationSweepFieldsSummary };
  elementDependenciesByOrg: { [crmOrgId: string]: DocumentationElementDependencies };
}

const initialState: SalesforceDocumentationState = {
  objects: {
    favorites: [],
    standardObjects: [],
    cpqObjects: [],
    customObjects: [],
    platformEvents: [],
  },
  singleObject: undefined,
  transientObjects: {},
  relatedListsAndButtons: {},
  showLoader: false,
  parsedByOrg: {},
  documentationLoadingStateByOrgId: {},
  saltoObjectTypesByOrg: {},
  sweepElementsByOrg: {},
  sweepFieldsByOrg: {},
  elementDependenciesByOrg: {},
};

export const salesforceDocumentationSlice = createSlice({
  name: 'salesforceDocumentation',
  initialState,
  reducers: {
    setObjects: (
      state,
      action: PayloadAction<{
        funnelsData: FunnelsData;
        objectTypeNames: ObjectTypeName[];
        pills?: DocumentationPills;
        transientObjects: { [objectName: string]: boolean };
        parsedObjects?: ObjectType[];
      }>,
    ) => {
      const { funnelsData, objectTypeNames, pills, transientObjects, parsedObjects } =
        action.payload;

      const {
        cpq,
        favorites,
        customObjectsNotInFM,
        platformEventsNotInFM,
        standardObjectsNotInFunnelMap,
      } = getObjectsWithPillsPerCategory({
        funnelsData,
        objectTypeNames,
        pills,
        transientObjects,
        parsedObjects,
      });

      state.objects = {
        favorites,
        standardObjects: standardObjectsNotInFunnelMap,
        cpqObjects: cpq,
        customObjects: customObjectsNotInFM,
        platformEvents: platformEventsNotInFM,
      };
    },
    setObjectName: (
      state,
      action: PayloadAction<{
        objectTypeName?: ObjectTypeName;
      }>,
    ) => {
      const { objectTypeName } = action.payload;

      state.singleObject = {
        objectTypeName: objectTypeName,
        activeTab: DocumentationTabTypes.CARDS_LIST,
      };
    },
    setSingleObjectName: (
      state,
      action: PayloadAction<{
        singleObjectName?: ObjectTypeName;
        tab: DocumentationTabTypes;
      }>,
    ) => {
      const { singleObjectName, tab } = action.payload;

      if (!singleObjectName) {
        state.singleObject = undefined;
        return;
      }

      state.singleObject = {
        objectTypeName: singleObjectName,
        activeTab: tab,
      };
    },
    clearDocumentationDialog: (state) => ({
      ...initialState,
      objects: state.objects,
      parsedByOrg: state.parsedByOrg,
      documentationLoadingStateByOrgId: state.documentationLoadingStateByOrgId,
      saltoObjectTypesByOrg: state.saltoObjectTypesByOrg,
      sweepElementsByOrg: state.sweepElementsByOrg,
    }),
    openPredefinedStepRulesInSingleObjectScreen: (
      state,
      action: PayloadAction<{
        singleObjectApiName: string;
        defaultActiveTab: DocumentationTabTypes;
        objects?: ObjectTypeName[];
      }>,
    ) => {
      const { singleObjectApiName, defaultActiveTab, objects } = action.payload;

      state.singleObject = {
        objectTypeName: objects?.find((object) => object.objectType === singleObjectApiName),
        activeTab: defaultActiveTab,
      };
    },
    handleOnPillClick: (
      state,
      action: PayloadAction<{
        tab: DocumentationTabTypes;
        singleObjectName: string;
      }>,
    ) => {
      state.singleObject = {
        objectTypeName: {
          objectType: action.payload.singleObjectName,
          label: action.payload.singleObjectName,
        },
        activeTab: action.payload.tab,
      };
    },
    setTab: (
      state,
      action: PayloadAction<{
        tab: DocumentationTabTypes;
      }>,
    ) => {
      state.singleObject = {
        ...state.singleObject,
        activeTab: action.payload.tab,
      };
    },
    setShowLoader: (state, action: PayloadAction<{ showLoader: boolean }>) => {
      state.showLoader = action.payload.showLoader;
    },
    setTransientObjects: (
      state,
      action: PayloadAction<{
        objectName: string;
        crmOrgId: string;
        isObjectBeingParsed: boolean;
      }>,
    ) => {
      const { crmOrgId, objectName, isObjectBeingParsed } = action.payload;

      if (!state.transientObjects[crmOrgId]) {
        state.transientObjects[crmOrgId] = {};
      }

      state.transientObjects[crmOrgId][objectName] = isObjectBeingParsed;
    },
    setActiveObjectElementsRawData: (
      state,
      action: PayloadAction<{
        list: { [key in DocumentationTabTypes]: SalesforceItem[] };
      }>,
    ) => {
      const { list } = action.payload;
      state.activeObjectElementsRawData = list;

      const newObjectOptions: { label: string; value: string }[] = uniq(
        flatten(
          list[DocumentationTabTypes.RELATED_OBJECTS]?.map(
            (relatedObject) => relatedObject.objectApiNames ?? [],
          ),
        ),
      )
        .filter((item) => !!item)
        .map((item) => ({ label: formatCamelCase(item ?? ''), value: item }));

      const configurationTypeOptions: { label: string; value: string }[] = uniq(
        flatten(
          list[DocumentationTabTypes.RELATED_OBJECTS]?.map(
            (relatedObject) => relatedObject.parentType,
          ),
        ),
      )
        .filter((item) => !!item)
        .map((item) => ({
          label: translateTypeToHumanName(item),
          value: item,
        }));

      const sortedNewObjectOptions = sortLexicographically({
        items: newObjectOptions,
        sortKey: 'label',
      });

      const sortedConfigurationTypeOptions = sortLexicographically({
        items: configurationTypeOptions,
        sortKey: 'label',
      });

      if (!isEqual(sortedNewObjectOptions, state.relatedObjectFilterOptions)) {
        state.relatedObjectFilterOptions = sortedNewObjectOptions;
      }

      if (!isEqual(sortedConfigurationTypeOptions, state.configurationTypeFilterOptions)) {
        state.configurationTypeFilterOptions = sortedConfigurationTypeOptions;
      }
    },
    setRelatedListsAndButtons: (
      state,
      action: PayloadAction<{
        metadataId: string;
        lists: RelatedListProperties[];
        buttons: NameProperties[];
      }>,
    ) => {
      const { metadataId, lists, buttons } = action.payload;
      state.relatedListsAndButtons[metadataId] = { lists, buttons };
    },
    beginDocumentationFetch: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      state.documentationLoadingStateByOrgId[action.payload.crmOrgId] = { status: 'loading' };
    },
    failDocumentationFetch: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;
      delete state.parsedByOrg[crmOrgId];
      state.documentationLoadingStateByOrgId[crmOrgId] = { status: 'error' };
    },
    setDocumentationData: (
      state,
      action: PayloadAction<{ crmOrgId: string; data: DocumentationParsedData }>,
    ) => {
      const { crmOrgId, data } = action.payload;
      state.parsedByOrg[crmOrgId] = data;
      state.documentationLoadingStateByOrgId[crmOrgId] = { status: 'success' };
    },
    setSaltoObjectTypes: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        types: Pick<ObjectTypeName, 'objectType' | 'label'>[];
      }>,
    ) => {
      const { crmOrgId, types } = action.payload;
      state.saltoObjectTypesByOrg[crmOrgId] = types;
    },
    setSweepElements: (
      state,
      action: PayloadAction<{ crmOrgId: string; sweepElements: SweepElementsByKey }>,
    ) => {
      const { crmOrgId, sweepElements } = action.payload;
      state.sweepElementsByOrg[crmOrgId] = sweepElements;
    },
    setSweepFields: (
      state,
      action: PayloadAction<{ crmOrgId: string; sweepFields: DocumentationSweepFieldsSummary }>,
    ) => {
      const { crmOrgId, sweepFields } = action.payload;
      state.sweepFieldsByOrg[crmOrgId] = sweepFields;
    },
    setElementDependencies: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        elementDependencies: DocumentationElementDependencies;
      }>,
    ) => {
      const { crmOrgId, elementDependencies } = action.payload;
      state.elementDependenciesByOrg[crmOrgId] = elementDependencies;
    },
    mergeObjectParsedData: (
      state,
      action: PayloadAction<{ crmOrgId: string; parsedData: ObjectParsedDataByType }>,
    ) => {
      const { crmOrgId, parsedData } = action.payload;
      const existing = state.parsedByOrg[crmOrgId] ?? {};

      if (existing.parsedObjectNames?.includes(parsedData.name)) return;

      const newConfigurationItems = calcConfigurationItemsFromParserResponse([parsedData]);
      const newRecordTypes = calcParsedRecordTypes([parsedData]);
      const newLayouts = calcLayouts([parsedData]);

      state.parsedByOrg[crmOrgId] = {
        ...existing,
        layouts: { ...(existing.layouts ?? {}), ...newLayouts },
        configurationItems: [...(existing.configurationItems ?? []), ...newConfigurationItems],
        onlyActiveConfigurationItems: [
          ...(existing.onlyActiveConfigurationItems ?? []),
          ...newConfigurationItems.filter(
            (item) => (item as { isActive?: boolean }).isActive !== false,
          ),
        ],
        parsedRecordTypes: [...(existing.parsedRecordTypes ?? []), ...newRecordTypes],
        parsedObjectNames: [...(existing.parsedObjectNames ?? []), parsedData.name],
        parserObjects: [
          ...(existing.parserObjects ?? []),
          { name: parsedData.name, favorite: parsedData.favorite },
        ],
      };
    },
    updateObjectFavorite: (
      state,
      action: PayloadAction<{ crmOrgId: string; objectName: string; favorite: boolean }>,
    ) => {
      const { crmOrgId, objectName, favorite } = action.payload;
      const orgData = state.parsedByOrg[crmOrgId];
      if (!orgData) return;

      if (!orgData.parserObjects) {
        orgData.parserObjects = [];
      }

      const obj = orgData.parserObjects.find((o) => o.name === objectName);
      if (obj) {
        obj.favorite = favorite;
        return;
      }

      if (favorite) {
        orgData.parserObjects.push({ name: objectName, favorite });
        if (!orgData.parsedObjectNames?.includes(objectName)) {
          orgData.parsedObjectNames = [...(orgData.parsedObjectNames ?? []), objectName];
        }
      }
    },
  },
});

export const {
  beginDocumentationFetch,
  failDocumentationFetch,
  setSingleObjectName,
  clearDocumentationDialog,
  openPredefinedStepRulesInSingleObjectScreen,
  setTab,
  handleOnPillClick,
  setActiveObjectElementsRawData,

  setObjectName,
  setObjects,

  setShowLoader,
  setTransientObjects,
  setRelatedListsAndButtons,
  setDocumentationData,
  setSaltoObjectTypes,
  setSweepElements,
  setSweepFields,
  setElementDependencies,
  mergeObjectParsedData,
  updateObjectFavorite,
} = salesforceDocumentationSlice.actions;

export const selectTransientObjects = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.transientObjects[crmOrgId];

export const selectDocumentationLoadingState =
  (crmOrgId: string) =>
  (state: RootState): DocumentationLoadingState =>
    state.salesforceDocumentation.documentationLoadingStateByOrgId[crmOrgId]?.status ?? 'idle';

export const selectIsDocumentationLoading = (crmOrgId: string) => (state: RootState) =>
  selectDocumentationLoadingState(crmOrgId)(state) === 'loading';

/** Full documentation API fetch in flight, or global parse-gap spinner while this org has no parsed blob yet. */
export const selectLoader = (crmOrgId: string) => (state: RootState) => {
  const doc = state.salesforceDocumentation;
  const bundleLoading = doc.documentationLoadingStateByOrgId[crmOrgId]?.status === 'loading';
  const parseGapLoader = doc.showLoader && !doc.parsedByOrg[crmOrgId];
  return bundleLoading || parseGapLoader;
};

export const selectSingleObject = (state: RootState) =>
  state.salesforceDocumentation.singleObject?.objectTypeName;

export const selectSingleObjectActiveTab = (state: RootState) =>
  state.salesforceDocumentation.singleObject?.activeTab;

export const selectObjects = (state: RootState) => state.salesforceDocumentation.objects;

export const selectDocumentationActiveObjectData = (state: RootState) =>
  state.salesforceDocumentation.activeObjectElementsRawData;

export const selectDocumentationActiveTabElements = (state: RootState) =>
  state.salesforceDocumentation.activeObjectElementsRawData?.[
    state.salesforceDocumentation.singleObject
      ?.activeTab as keyof typeof state.salesforceDocumentation.activeObjectElementsRawData
  ];

export const selectRelatedObjectFilterOptions = (state: RootState) =>
  state.salesforceDocumentation.relatedObjectFilterOptions;

export const selectRelatedObjectConfigurationTypeFilterOptions = (state: RootState) =>
  state.salesforceDocumentation.configurationTypeFilterOptions;

export const selectRelatedListsAndButtons = (metadataId: string) => (state: RootState) =>
  state.salesforceDocumentation.relatedListsAndButtons[metadataId];

// Selectors migrated from parserReducer.ts
export const selectParserInfo = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.parsedByOrg[crmOrgId];

export const selectIsOrgFullyFetched = (crmOrgId: string) => (state: RootState) =>
  selectDocumentationLoadingState(crmOrgId)(state) === 'success';

export const selectParsedFields = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.parsedByOrg[crmOrgId]?.parsedFields;

export const selectParsedRecordTypes = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.parsedByOrg[crmOrgId]?.parsedRecordTypes;

export const selectDocumentationConfigurationItems = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.parsedByOrg[crmOrgId]?.configurationItems;

export const selectParsedObjects = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.parsedByOrg[crmOrgId]?.parserObjects;

export const selectSaltoObjectTypes = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.saltoObjectTypesByOrg[crmOrgId];

export const selectonlyActiveConfigurationItems = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.parsedByOrg[crmOrgId]?.onlyActiveConfigurationItems;

export const selectSweepElements = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.sweepElementsByOrg[crmOrgId];

export const selectDocumentationSweepFields = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.sweepFieldsByOrg[crmOrgId];

export const selectDocumentationElementDependencies = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.elementDependenciesByOrg[crmOrgId];

// Cache selector instances per crmOrgId to preserve memoization
const relatedObjectsSelectorsCache = new Map<
  string,
  (state: RootState) => RelatedObjectsMap | undefined
>();

export const selectRelatedObjectsByObjectName = (
  crmOrgId: string,
): ((state: RootState) => RelatedObjectsMap | undefined) => {
  if (!relatedObjectsSelectorsCache.has(crmOrgId)) {
    relatedObjectsSelectorsCache.set(
      crmOrgId,
      createSelector(
        [(state: RootState) => state.salesforceDocumentation.parsedByOrg[crmOrgId]?.relatedObjects],
        (relatedObjects) => relatedObjects,
      ),
    );
  }
  return relatedObjectsSelectorsCache.get(crmOrgId)!;
};

export const selectLayoutsByObjectName = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.parsedByOrg[crmOrgId]?.layouts;

export default salesforceDocumentationSlice.reducer;
